// src/components/LocationsPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Box,
    Grid,
    useMediaQuery,
    CircularProgress,
    Alert // Import Alert for displaying messages
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
// Assume these exist and are imported correctly from api.js
import { fetchLocations, createLocation, updateLocation, deleteLocation } from '../api';
import LocationForm from './LocationForm'; // Import the new component


function LocationsPage() {
    const [data, setData] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        id: '',
        StoreName: '',
        CSID: '',
        StoreAddress: {
            FullName: '',
            AddressLine1: '',
            AddressLine2: '',
            AddressLine3: '',
            CityName: '',
            StateName: '',
            ZipCode: ''
        },
        StoreManagerEmail: '',
        GSTIN: '',
        GST: '',
        CompanyPAN: '',
        StorePhoneNumber: ''
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true); // Initial data loading state
    const [error, setError] = useState(null);       // Initial data loading error

    const [actionLoading, setActionLoading] = useState(false); // State for dialog actions loading
    const [deletingId, setDeletingId] = useState(null); // State to track which item is being deleted
    const [validationErrors, setValidationErrors] = useState({});

    const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));


    // Refs for potential focus on error
    const storeNameRef = useRef(null);
    const csidRef = useRef(null);
    const fullNameRef = useRef(null);
    const addressLine1Ref = useRef(null); // Added ref for AddressLine1
    const addressLine2Ref = useRef(null); // Added ref for AddressLine2
    const addressLine3Ref = useRef(null); // Added ref for AddressLine3
    const cityRef = useRef(null);
    const stateRef = useRef(null); // Added ref for StateName
    const zipCodeRef = useRef(null); // Added ref for ZipCode
    const emailRef = useRef(null); // Added ref for StoreManagerEmail
    const gstinRef = useRef(null); // Added ref for GSTIN
    const gstRef = useRef(null); // Added ref for GST
    const companyPANRef = useRef(null); // Added ref for CompanyPAN
    const phoneRef = useRef(null); // Added ref for StorePhoneNumber


    // --- Mapping of validation error keys to refs ---
    const fieldRefs = {
        StoreName: storeNameRef,
        CSID: csidRef,
        'StoreAddress.FullName': fullNameRef,
        'StoreAddress.AddressLine1': addressLine1Ref, // Add to map
        'StoreAddress.AddressLine2': addressLine2Ref, // Add to map
        'StoreAddress.AddressLine3': addressLine3Ref, // Add to map
        'StoreAddress.CityName': cityRef,
        'StoreAddress.StateName': stateRef, // Add to map
        'StoreAddress.ZipCode': zipCodeRef, // Add to map
        StoreManagerEmail: emailRef, // Add to map
        GSTIN: gstinRef, // Add to map
        GST: gstRef, // Add to map
        CompanyPAN: companyPANRef, // Add to map
        StorePhoneNumber: phoneRef, // Add to map
        // Add entries for other fields as you add validation for them
    };

    // Function to load data from the API
    const loadData = async () => {
        setLoading(true); // Start loading
        setError(null);  // Clear previous error on new load
        try {
            // Assuming fetchLocations is implemented in api.js and returns { code: 0, data: [...] }
            const apiResponse = await fetchLocations();
            if (apiResponse.code === 0) {
                // **IMPORTANT**: Adjust property names here based on your backend's GET response casing!
                // Example: if backend returns { storeName: '...', csid: '...', storeAddress: { ... } }
                // You might need to map them to your formData casing or adjust the table display accessors.
                setData(apiResponse.data || []); // Ensure data is an array
            } else {
                setError(apiResponse.message || 'Failed to load locations.');
                setData([]); // Clear data on error
            }
        } catch (err) {
            console.error("Error loading locations:", err);
            setError(err.message || 'Failed to load locations.');
            setData([]); // Clear data on error
        } finally {
            setLoading(false); // End loading
        }
    };

    useEffect(() => {
        // Load data when the component mounts
        loadData();
    }, []); // Empty dependency array means this runs only once on mount

    const handleOpenDialog = () => {
        setOpenDialog(true);
        // Reset form data to initial empty state
        setFormData({
            id: '',
            StoreName: '',
            CSID: '',
            StoreAddress: {
                FullName: '',
                AddressLine1: '',
                AddressLine2: '',
                AddressLine3: '',
                CityName: '',
                StateName: '',
                ZipCode: ''
            },
            StoreManagerEmail: '',
            GSTIN: '',
            GST: '',
            CompanyPAN: '',
            StorePhoneNumber: ''
        });
        setIsEdit(false);
        setValidationErrors({}); // Clear validation errors when opening dialog

    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setActionLoading(false); // Reset action loading on close
        setValidationErrors({}); // Clear validation errors when closing dialog

    };

    // Handle changes for both top-level and nested StoreAddress fields
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Clear the validation error for this specific field as the user types
        setValidationErrors(prev => ({ ...prev, [name]: undefined }));
        // Check if the field name includes a dot (for nested StoreAddress fields)
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            // Handle top-level fields
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Basic client-side validation for demonstration
    const validateForm = () => {
        const errors = {};
        if (!formData.StoreName) errors.StoreName = 'Store Name is required';
        if (!formData.CSID) errors.CSID = 'CSID is required';
        if (!formData.StoreAddress.FullName) errors['StoreAddress.FullName'] = 'Address Full Name is required';
        if (!formData.StoreAddress.CityName) errors['StoreAddress.CityName'] = 'City is required';
        // TODO: Add more validation as needed for other fields (e.g., email format, zip code format)
        setValidationErrors(errors); // **Set the validation errors state**

        return errors;
    };

    // --- Helper function to focus the first field with an error ---
    const focusFirstError = (errors) => {
        const firstErrorField = Object.keys(errors)[0];
        const refToFocus = fieldRefs[firstErrorField];
        if (refToFocus && refToFocus.current) {
            // Using setTimeout allows the UI to update with error messages first
            setTimeout(() => {
                refToFocus.current.focus();
            }, 0); // Small delay can sometimes be helpful
        }
    };

    const handleCreate = async () => {
        const errors = validateForm(); // Perform client-side validation

        if (Object.keys(errors).length > 0) {
            console.error("Validation Errors:", errors);
            focusFirstError(errors); // Focus the first field with error
            return; // Stop submission
        }

        setActionLoading(true); // Start dialog action loading
        try {
            // Assuming createLocation is implemented in api.js and expects { data: {...} } payload
            await createLocation({
                data: {
                    StoreName: formData.StoreName,
                    CSID: formData.CSID,
                    StoreAddress: formData.StoreAddress, // Send the nested object
                    StoreManagerEmail: formData.StoreManagerEmail,
                    GSTIN: formData.GSTIN,
                    GST: formData.GST,
                    CompanyPAN: formData.CompanyPAN,
                    StorePhoneNumber: formData.StorePhoneNumber
                    // Add any other required fields from the payload
                },
            });
            // **Instead of manual state update, re-fetch all data:**
            await loadData(); // Re-fetch all locations after successful creation
            handleCloseDialog(); // Close the dialog after re-fetching and updating table
        } catch (err) {
            console.error("Create location failed:", err);
            setError(err.message || 'Failed to create location.');
            // TODO: Handle API errors and potentially display them in the UI
        } finally {
            setActionLoading(false); // End dialog action loading
        }
    };

    const handleEdit = (id) => {
        // Find the item in the currently loaded data
        const locationToEdit = data.find((location) => location.id === id);
        if (locationToEdit) {
            setFormData({
                ...locationToEdit,
                // **IMPORTANT**: When setting formData from backend data,
                // ensure you map backend property names (which might be lowercase/camelCase)
                // to your formData property names (PascalCase).
                // Also, ensure nested objects are copied correctly.
                StoreName: locationToEdit.storeName, // Example: Map backend `storeName` to formData `StoreName`
                CSID: locationToEdit.csid,             // Example: Map backend `csid` to formData `CSID`
                StoreAddress: {
                    // Example: Map backend nested names
                    FullName: locationToEdit.storeAddress?.fullName || '',
                    AddressLine1: locationToEdit.storeAddress?.addressLine1 || '',
                    AddressLine2: locationToEdit.storeAddress?.addressLine2 || '',
                    AddressLine3: locationToEdit.storeAddress?.addressLine3 || '',
                    CityName: locationToEdit.storeAddress?.cityName || '',
                    StateName: locationToEdit.storeAddress?.stateName || '',
                    ZipCode: locationToEdit.storeAddress?.zipCode || ''
                },
                StoreManagerEmail: locationToEdit.storeManagerEmail || '', // Example: Map backend `storeManagerEmail`
                GSTIN: locationToEdit.GSTIN || '',
                GST: locationToEdit.GST || '',
                CompanyPAN: locationToEdit.companyPAN || '',
                StorePhoneNumber: locationToEdit.storePhoneNumber || '' // Example: Map backend `storePhoneNumber`
            });
            setSelectedId(id);
            setIsEdit(true);
            setOpenDialog(true);
            setValidationErrors({}); // Clear validation errors when opening for edit

        }
    };

    const handleUpdate = async () => {
        const errors = validateForm(); // Perform client-side validation
        if (Object.keys(errors).length > 0) {
            console.error("Validation Errors:", errors);
            focusFirstError(errors); // Focus the first field with error
            return; // Stop submission
        }

        setActionLoading(true); // Start dialog action loading
        try {
            // Assuming updateLocation is implemented in api.js and expects { data: {...} } payload and ID in URL
            await updateLocation(selectedId, {
                data: {
                    StoreName: formData.StoreName,
                    CSID: formData.CSID,
                    StoreAddress: formData.StoreAddress, // Send the nested object
                    StoreManagerEmail: formData.StoreManagerEmail,
                    GSTIN: formData.GSTIN,
                    GST: formData.GST,
                    CompanyPAN: formData.CompanyPAN,
                    StorePhoneNumber: formData.StorePhoneNumber
                    // Add any other required fields from the payload
                },
            });
            // **Instead of manual state update, re-fetch all data:**
            await loadData(); // Re-fetch all locations after successful update
            handleCloseDialog(); // Close the dialog after re-fetching and updating table
        } catch (err) {
            console.error("Update location failed:", err);
            setError(err.message || 'Failed to update location.');
            // TODO: Handle API errors and potentially display them in the UI
        } finally {
            setActionLoading(false); // End dialog action loading
        }
    };

    const handleDelete = async (id) => {
        // Optional: Add a confirmation dialog before deleting
        if (!window.confirm(`Are you sure you want to delete location with ID ${id}?`)) {
            return; // User cancelled deletion
        }

        setDeletingId(id); // Set deleting state for this specific item
        try {
            // Assuming deleteLocation is implemented in api.js and uses ID in URL
            await deleteLocation(id);
            // **Instead of manual state update, re-fetch all data:**
            await loadData(); // Re-fetch all locations after successful deletion
            // Optional: Show a success message after re-fetch completes
        } catch (err) {
            console.error("Delete location failed:", err);
            setError(err.message || 'Failed to delete location.');
            // TODO: Handle API errors and potentially display them
        } finally {
            setDeletingId(null); // Clear deleting state
        }
    };

    if (loading) {
        return <Typography align="center" mt={5}>Loading locations...</Typography>;
    }

    if (error) {
        return <Typography color="error" align="center" mt={5}>Error loading locations: {error}</Typography>;
    }

    return (
        <Box sx={{ p: 3, mt: 3 }}>
            <Typography variant="h4" gutterBottom>
                Locations Management
            </Typography>

            {/* Create New Button */}
            <Box sx={{ mb: 2 }}>
                <Button variant="contained" color="primary" onClick={handleOpenDialog}>
                    Add New Location
                </Button>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table sx={{ minWidth: isMobile ? 300 : 650 }} aria-label="locations table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Location Name</TableCell>
                            <TableCell>Location ID</TableCell>
                            <TableCell>Full Address Name</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Zip Code</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row) => (
                            // **IMPORTANT**: Ensure the key prop uses a unique ID from your backend data
                            <TableRow key={row.id}>
                                {/* <TableCell component="th" scope="row"> */}
                                    {/* {row.id} Display ID if available */}
                                {/* </TableCell> */}
                                {/* **IMPORTANT**: Adjust property accessors here based on your backend's GET response casing! */}
                                {/* Example: if backend returns { storeName: '...', storeAddress: { fullName: '...' } } */}
                                <TableCell>{row.storeName}</TableCell> {/* Use the actual backend property name */}
                                <TableCell>{row.csid}</TableCell>
                                <TableCell>{row.storeAddress?.fullName}</TableCell> {/* Use optional chaining and backend property name */}
                                <TableCell>{row.storeAddress?.cityName}</TableCell> {/* Use optional chaining and backend property name */}
                                <TableCell>{row.storeAddress?.stateName}</TableCell> {/* Use optional chaining and backend property name */}
                                <TableCell>{row.storeAddress?.zipCode}</TableCell> {/* Use optional chaining and backend property name */}
                                <TableCell>{row.storePhoneNumber}</TableCell> {/* Use the actual backend property name */}
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            onClick={() => handleEdit(row.id)}
                                            size="small"
                                            variant="outlined"
                                            disabled={actionLoading || deletingId !== null} // Disable if dialog action or any delete is in progress
                                        >
                                            <EditIcon />
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(row.id)}
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={actionLoading || deletingId === row.id} // Disable if dialog action or *this* item is being deleted
                                            startIcon={deletingId === row.id ? <CircularProgress size={16} color="inherit" /> : null} // Show spinner if deleting this item
                                        >
                                            {deletingId === row.id ? 'Deleting...' : <DeleteIcon />}
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog for Create/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>{isEdit ? 'Edit Location' : 'Add New Location'}</DialogTitle>
                <DialogContent>
                    {/* --- Use the memoized LocationForm component --- */}
                    <LocationForm
                        formData={formData}
                        handleInputChange={handleInputChange}
                        validationErrors={validationErrors} // Pass validation errors down
                        actionLoading={actionLoading}
                        isEdit={isEdit}
                        // Pass refs down if they are used in LocationForm for focus (optional, depends on focus implementation)
                        storeNameRef={storeNameRef}
                        csidRef={csidRef}
                        fullNameRef={fullNameRef}
                        addressLine1Ref={addressLine1Ref}
                        addressLine2Ref={addressLine2Ref}
                        addressLine3Ref={addressLine3Ref}
                        cityRef={cityRef}
                        stateRef={stateRef}
                        zipCodeRef={zipCodeRef}
                        emailRef={emailRef}
                        gstinRef={gstinRef}
                        gstRef={gstRef}
                        companyPANRef={companyPANRef}
                        phoneRef={phoneRef}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="secondary" disabled={actionLoading}>Cancel</Button>
                    <Button
                        onClick={isEdit ? handleUpdate : handleCreate}
                        color="primary"
                        variant="contained"
                        disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {actionLoading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
}

export default LocationsPage;